import type React from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@meno/ui/components/ui/button'
import { SectionSurface } from './settings-primitives'

// Collapsed channel row for the screen preview's channels
// (Stop Screen / Screen Content / Audio Track / Transitions). It never expands
// inline — the header shows an icon, a title, and a one-line "now playing"
// summary, and a trailing "Change" button opens that channel's modal. Neutral
// styling only — no brand-red highlight — so every channel reads as the same
// calm row. When `onRemove` is provided (and the channel has an occupant), an X
// next to Change clears the channel.
export default function ChannelCard({
  icon: Icon, title, summary, changeLabel, onChange, onRemove,
}: {
  icon: React.ElementType
  title: string
  /** One-line current-selection summary shown next to the title. */
  summary?: string
  /** Label for the trailing button (e.g. "Change" / "Add" / "Stream"). */
  changeLabel: string
  /** Opens the channel's edit modal. */
  onChange: () => void
  /** Clears the channel's occupant. Omit (or pass undefined when empty) to
   *  hide the remove control. */
  onRemove?: () => void
}) {
  const { t } = useTranslation()
  return (
    <SectionSurface className="flex items-center gap-2 px-4 py-2.5 min-w-0">
      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
      <span className="text-sm font-medium shrink-0">{title}</span>
      <span className="text-xs text-muted-foreground truncate flex-1 min-w-0" title={summary}>
        {summary}
      </span>
      <Button size="sm" variant="outline" className="shrink-0" onClick={onChange}>
        {changeLabel}
      </Button>
      {onRemove && (
        <Button
          size="icon"
          variant="outline"
          className="shrink-0 h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          aria-label={t('common.remove', 'Remove')}
          title={t('screens.removeContent', 'Remove from this screen')}
        >
          <X className="w-4 h-4" />
        </Button>
      )}
    </SectionSurface>
  )
}
